// pages/PropertyPage/PropertyPage.jsx
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BsTelephone, BsGeoAlt, BsArrowUpRight, BsArrowLeft, BsX, BsChevronLeft, BsChevronRight, BsFlower1 } from 'react-icons/bs';
import SectionTitle from '../../components/SectionTitle/SectionTitle';
import useScrollReveal from '../../hooks/useScrollReveal';
import { getPropertyById, universalAmenities } from '../../data/properties';
import './PropertyPage.css';

const parsePrice = (priceStr) => Number(priceStr.replace(/[^0-9]/g, '')) || 0;

// Spa services data (matching the Spa page)
const spaServices = [
  {
    id: 1,
    title: "Abhyanga",
    subtitle: "Traditional Full-Body Oil Massage",
    description: "A rhythmic, warm-oil massage using medicated herbal oils chosen for your dosha — the foundation of every Kerala Ayurvedic healing ritual.",
    price: "₹3,500",
    duration: "60 min",
    image: "/assets/mas1.jpg"
  },
  {
    id: 2,
    title: "Sirodhara",
    subtitle: "Continuous Warm Oil Stream Therapy",
    description: "A steady stream of warm herbal oil poured over the forehead in slow, unbroken rhythm — deeply calming for the nervous system.",
    price: "₹4,800",
    duration: "50 min",
    image: "/assets/mas2.jpg"
  },
  {
    id: 3,
    title: "Pizhichil",
    subtitle: "The Royal Oil Bath",
    description: "Once reserved for Kerala royalty — warm medicated oil is continuously poured over the body with synchronized massage strokes.",
    price: "₹6,500",
    duration: "75 min",
    image: "/assets/mas3.jpg"
  }
];

export default function PropertyPage() {
  const { id } = useParams();
  const property = getPropertyById(id);
  const tiersRef = useScrollReveal();
  const galleryRef = useScrollReveal();
  const spaRef = useScrollReveal();
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Check if this is the main Kanjikode property
  const isMainBranch = property?.id === 'salkara-inn-main';

  // Cheapest tier drives the "Starting from" figure + the Best Value badge
  const cheapestTierId = useMemo(() => {
    if (!property) return null;
    return property.roomTiers.reduce((min, tier) =>
      parsePrice(tier.price) < parsePrice(property.roomTiers.find((t) => t.id === min).price) ? tier.id : min,
      property.roomTiers[0].id
    );
  }, [property]);

  const startingPrice = useMemo(() => {
    if (!property) return null;
    return Math.min(...property.roomTiers.map((t) => parsePrice(t.price)));
  }, [property]);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(
    (e) => {
      e?.stopPropagation();
      setLightboxIndex((i) => (i === null ? null : (i - 1 + property.gallery.length) % property.gallery.length));
    },
    [property]
  );
  const showNext = useCallback(
    (e) => {
      e?.stopPropagation();
      setLightboxIndex((i) => (i === null ? null : (i + 1) % property.gallery.length));
    },
    [property]
  );

  // Keyboard nav for the lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, closeLightbox, showPrev, showNext]);

  // No matching property → send back to home instead of a blank page
  if (!property) {
    return <Navigate to="/" replace />;
  }

  // Use property.mapLink if available, otherwise fallback to address-based search
  const mapsHref = property.mapLink || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.address)}`;

  return (
    <>
      <section className={`property-hero ${isMainBranch ? 'property-hero--main' : ''}`}>
        <img src={property.heroImage} alt={property.name} />
        <div className="property-hero__overlay" />

        {isMainBranch && (
          <div className="property-hero__badge">
            <BsFlower1 /> Flagship Wellness Center
          </div>
        )}

        <div className="container property-hero__top">
          <Link to="/" className="property-hero__back">
            <BsArrowLeft /> All Properties
          </Link>
        </div>

        <div className="container property-hero__content">
          <span className="eyebrow">{property.tagline}</span>
          <h1>{property.name}</h1>
          <a href={mapsHref} target="_blank" rel="noreferrer" className="property-hero__address">
            <BsGeoAlt /> {property.address}
          </a>
        </div>
      </section>

      {/* Quick-glance bar: price, room count, jump links, and a call button all above the fold */}
      <div className="property-quickbar">
        <div className="container property-quickbar__inner">
          <div className="property-quickbar__stat">
            <span className="property-quickbar__label">Starting from</span>
            <span className="property-quickbar__value">₹ {startingPrice.toLocaleString('en-IN')}<small> / night</small></span>
          </div>
          <div className="property-quickbar__stat">
            <span className="property-quickbar__label">Room Types</span>
            <span className="property-quickbar__value">{property.roomTiers.length}</span>
          </div>
          <nav className="property-quickbar__links">
            <a href="#rooms">Rooms</a>
            <a href="#gallery">Gallery</a>
            {isMainBranch && <a href="#spa">Spa</a>}
            <a href="#contact">Contact</a>
          </nav>
          <a href={`tel:+91${property.contacts[0]}`} className="btn btn-gold property-quickbar__call">
            <BsTelephone /> Call Now
          </a>
        </div>
      </div>

      <section id="gallery" className="section property-gallery">
        <div className="container">
          <SectionTitle eyebrow="Gallery" title={`Inside ${property.name}`} />
          <div ref={galleryRef} className="reveal property-gallery__grid">
            {property.gallery.map((src, i) => (
              <motion.div
                key={src}
                className="property-gallery__item"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.4 }}
                onClick={() => setLightboxIndex(i)}
              >
                <img src={src} alt={`${property.name} photo ${i + 1}`} loading="lazy" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Lightbox with close button, counter, and prev/next navigation */}
        {lightboxIndex !== null && (
          <div className="property-gallery__lightbox" onClick={closeLightbox}>
            <button className="property-gallery__lightbox-close" onClick={closeLightbox} aria-label="Close">
              <BsX />
            </button>

            <button className="property-gallery__lightbox-nav property-gallery__lightbox-nav--prev" onClick={showPrev} aria-label="Previous image">
              <BsChevronLeft />
            </button>

            <img
              src={property.gallery[lightboxIndex]}
              alt={`${property.name} enlarged ${lightboxIndex + 1}`}
              onClick={(e) => e.stopPropagation()}
            />

            <button className="property-gallery__lightbox-nav property-gallery__lightbox-nav--next" onClick={showNext} aria-label="Next image">
              <BsChevronRight />
            </button>

            <span className="property-gallery__lightbox-count">
              {lightboxIndex + 1} / {property.gallery.length}
            </span>
          </div>
        )}
      </section>



      <section id="rooms" className="section property-tiers">
        <div className="container">
          <SectionTitle
            eyebrow="Room Options"
            title="Choose Your Room"
            subtitle={`${property.roomTiers.length} room ${property.roomTiers.length === 1 ? 'type' : 'types'} available at this property`}
          />

          <div ref={tiersRef} className="reveal property-tiers__grid">
            {property.roomTiers.map((tier) => (
              <div key={tier.id} className={`tier-card ${tier.id === cheapestTierId ? 'tier-card--best' : ''}`}>
                {tier.id === cheapestTierId && <span className="tier-card__badge">Best Value</span>}

                <div className="tier-card__header">
                  <h3>{tier.type}</h3>
                  <span className="tier-card__price">
                    {tier.price}
                    <small>{tier.priceUnit}</small>
                  </span>
                </div>

                <p className="tier-card__count">{tier.count} rooms available</p>
                {tier.roomSize && <p className="tier-card__size">{tier.roomSize}</p>}

                {tier.amenities?.length > 0 && (
                  <ul className="tier-card__amenities">
                    {tier.amenities.map((amenity) => (
                      <li key={amenity}>{amenity}</li>
                    ))}
                  </ul>
                )}

                {tier.note && <p className="tier-card__note">{tier.note}</p>}

                <a href={`tel:+91${property.contacts[0]}`} className="tier-card__cta">
                  Enquire about this room <BsArrowUpRight />
                </a>
              </div>
            ))}
          </div>

          {/* Facilities included in every room across every property */}
          <div className="property-tiers__universal">
            <h4>Included with every room</h4>
            <ul>
              {universalAmenities.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          {property.notes && <p className="property-tiers__notes">{property.notes}</p>}
        </div>
      </section>

      <section id="contact" className="property-contact">
        <div className="container property-contact__inner">
          <h2>Book a room at {property.name}</h2>
          <p className="property-contact__address">
            <BsGeoAlt /> {property.address}
          </p>
          <div className="property-contact__numbers">
            {property.contacts.map((number) => (
              <a key={number} href={`tel:+91${number}`} className="btn btn-outline">
                <BsTelephone /> {number}
              </a>
            ))}
          </div>
          <div className="property-contact__actions">
            <a href={mapsHref} target="_blank" rel="noreferrer" className="btn btn-outline">
              Get Directions <BsArrowUpRight />
            </a>
            <Link to="/contact" className="btn btn-gold">
              Enquire Online <BsArrowUpRight />
            </Link>
          </div>
        </div>
      </section>

            {/* SPA SECTION - Only visible for the main branch */}
      {isMainBranch && (
        <section id="spa" className="section property-spa">
          <div className="container">
            <div className="reveal">
              <div className="property-spa__header">
                <span className="eyebrow property-spa__eyebrow">
                  <BsFlower1 /> Kerala Ayurveda & Panchakarma
                </span>
                <SectionTitle 
                  eyebrow="Wellness at Salkara" 
                  title="Ayurvedic Spa & Wellness Center" 
                  subtitle="Authentic Kerala massage therapies and Panchakarma treatments at our flagship property"
                />
              </div>

              <div className="property-spa__grid">
                {spaServices.map((service) => (
                  <motion.div 
                    key={service.id} 
                    className="property-spa__card"
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="property-spa__card-image">
                      <img src={service.image} alt={service.title} loading="lazy" />
                      {/* <span className="property-spa__card-price">{service.price}</span> */}
                    </div>
                    <div className="property-spa__card-content">
                      <h3>{service.title}</h3>
                      <span className="property-spa__card-subtitle">{service.subtitle}</span>
                      <p className="property-spa__card-description">{service.description}</p>
                      <div className="property-spa__card-meta">
                        <span className="property-spa__card-duration">⏱ {service.duration}</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="property-spa__cta">
                <Link to="/spa" className="btn btn-gold btn-large">
                  View All Spa Treatments <BsArrowUpRight />
                </Link>
                <p className="property-spa__cta-subtext">
                  Explore our complete menu of Ayurvedic therapies at the Salkara Wellness Center
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Persistent mobile call bar so the number is always one tap away */}
      <a href={`tel:+91${property.contacts[0]}`} className="property-mobile-call">
        <BsTelephone /> Call {property.name}
      </a>
    </>
  );
}